import './applications.css';
import { parseCapabilityURN } from './parseCapabilityURN.js';
import { avatarHue } from './agentAvatar.js';

/**
 * grantSummary builds the grant count summary line for a single principal.
 *
 * Single allow grant:  "1 capability · Accounts · List"
 * Multiple grants, domains present: "3 capabilities · Accounts, Transactions, Liabilities"
 * Multiple grants, >3 unique domains: "4 capabilities · Accounts, Transactions +2 more"
 * Fallback (no domain parsed): "N capabilities"
 *
 * @param {Array<{ capabilityID: string, decision: string }>} grants
 * @returns {string}
 */
function grantSummary(grants) {
  const allowGrants = grants.filter((g) => g.decision === 'allow');
  if (allowGrants.length === 0) return '';

  const parsed = allowGrants.map((g) => parseCapabilityURN(g.capabilityID));

  if (allowGrants.length === 1) {
    const p = parsed[0];
    if (p.domain && p.action) {
      const domainTitle = p.domain
        .split(/[_-]/)
        .filter(Boolean)
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(' ');
      const actionTitle = p.action
        .split(/[_-]/)
        .filter(Boolean)
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(' ');
      return `1 capability · ${domainTitle} · ${actionTitle}`;
    }
    // 5-seg or fallback — use label minus the "action" portion if available
    return `1 capability`;
  }

  // Multiple grants — collect unique domain labels, sorted alphabetically
  const seen = new Set();
  const domainTitles = [];
  for (const p of parsed) {
    if (p.domain && !seen.has(p.domain)) {
      seen.add(p.domain);
      const domainTitle = p.domain
        .split(/[_-]/)
        .filter(Boolean)
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(' ');
      domainTitles.push(domainTitle);
    }
  }
  const domains = domainTitles.sort((a, b) => a.localeCompare(b));

  const count = allowGrants.length;
  const capWord = `${count} ${count === 1 ? 'capability' : 'capabilities'}`;

  if (domains.length === 0) return capWord;

  if (domains.length <= 3) {
    return `${capWord} · ${domains.join(', ')}`;
  }
  const shown = domains.slice(0, 3);
  const rest = domains.length - 3;
  return `${capWord} · ${shown.join(', ')} +${rest} more`;
}

/**
 * AgentRow renders a single agent row in the populated Act tab.
 */
function AgentRow({ principal }) {
  const { name: urn, displayName, grants = [] } = principal;
  const label = displayName || urn;
  const monogram = (label || '?').charAt(0).toUpperCase();
  const { bg, fg } = avatarHue(urn);
  const summary = grantSummary(grants);

  return (
    <div className="agent-row">
      <div
        className="avatar"
        style={{ background: bg, color: fg }}
        aria-hidden="true"
      >
        {monogram}
      </div>
      <div className="agent-info">
        <div className="agent-name">{label}</div>
        {summary && <div className="agent-summary">{summary}</div>}
        <div className="agent-urn">{urn}</div>
      </div>
      {/* TODO(JOBE-0148.4): confirm chat route once chat router is wired */}
      <a
        href={`/chat/new?agent=${encodeURIComponent(urn)}`}
        className="cta-link"
      >
        Chat →
      </a>
    </div>
  );
}

/**
 * ActTab — agent picker (Board 06 V1).
 *
 * Populated state: lists principals with at least one allow grant, each
 * as a "Chat →" row. Data comes from the same access endpoint as AccessTab.
 *
 * Empty state: proper .empty-state block when no principals have allow grants.
 *
 * Loading / error states mirror AccessTab.
 */
export function ActTab({ app, access }) {
  // Loading state
  if (access?.status === 'loading') {
    return (
      <div className="act-skeleton" aria-busy="true" aria-label="Loading agents…">
        <div className="skeleton-row pulse" />
      </div>
    );
  }

  // Error state
  if (access?.status === 'error') {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center' }}>
        Could not load access data.
      </div>
    );
  }

  // Filter to principals with at least one allow grant
  const allowedPrincipals = (access?.data?.principals ?? [])
    .filter((p) => (p.grants ?? []).some((g) => g.decision === 'allow'))
    .sort((a, b) => {
      const aName = a.displayName || a.name || '';
      const bName = b.displayName || b.name || '';
      return aName.localeCompare(bName);
    });

  // Empty state
  if (allowedPrincipals.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon" aria-hidden="true">○</div>
        <div className="empty-state-heading">No agents granted</div>
        <div className="empty-state-body">Grant access on the Access tab first.</div>
      </div>
    );
  }

  // Docs links — collect integrations that have a providerDocsURL (JOBE-0151 + PR #972).
  const docsLinks = (app.integrations || [])
    .filter((integ) => integ.providerDocsURL)
    .map((integ) => ({
      url: integ.providerDocsURL,
      label: integ.providerDisplayName || integ.providerRef || integ.type || 'Provider',
    }));

  // Populated state — agent picker list
  return (
    <div className="act-agent-list">
      <div className="act-section-header">Start a conversation</div>
      {allowedPrincipals.map((p) => (
        <AgentRow key={p.name} principal={p} />
      ))}
      {docsLinks.length > 0 && (
        <div className="docs-card">
          <div className="docs-card-header">Documentation</div>
          {docsLinks.map(({ url, label }) => (
            <a
              key={url}
              href={url}
              className="docs-card-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the {label} docs →
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
